import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material'
import { withFirebase } from '../Fb'
import moment from 'moment'
import { centsToInput, paymentSuggestion } from '../utils/activityPayment'

// Only cashier for now; the manager records a payment taken at the desk. More types are
// coming, and this list is the single place to add them - the dialog already renders a
// select rather than hardcoding the one value.
// The label must match what the backend stores as typelabel, because that is what the
// Maksutapa column renders. activities-addPayment writes 'Kassamaksu' - note event ticket
// cashier payments use 'Kassamyynti' instead, so the two are not interchangeable.
export const ACTIVITY_PAYMENT_TYPES = [{ value: 'cashier', label: 'Kassamaksu' }]

const DEFAULT_TYPE = 'cashier'

// activity.price is euros as a float; payments[].amount and pricing.price are cents. The field
// is euros, and the conversion happens once on save.
const formatCents = (cents) => (cents / 100).toFixed(2).replace('.', ',') + ' €'

const SOURCE_TEXT = {
  member: 'jäsenhinta',
  entry: 'jäsenyyden vapaa kerta',
  list: 'aktiviteetin hinta',
}

// activity.name is localized - { fi, sv, en } - not a string. Rendering it raw throws
// "Objects are not valid as a React child".
function activityName(activity) {
  const name = activity && activity.name
  if (!name) return ''
  if (typeof name === 'string') return name
  return name.fi || name.en || name.sv || ''
}

// How the backend is told WHICH participant to attach the payment to, in order of
// preference. Only one of the three is ever sent.
//
//   paymenttoken     the enrolment token, when the participant record carries one
//   entrytime        the enrolment timestamp, for the participants that do not
//   participantIndex the position in activity.participants[], last resort
//
// 17 of 42 participants in the current data have no paymenttoken, so the fallbacks are
// the normal path rather than an edge case.
function participantIdentity(activity, participant) {
  if (!participant) return {}
  if (participant.paymenttoken) return { paymenttoken: participant.paymenttoken }
  if (participant.entry) return { entrytime: participant.entry }
  // The grid rows ARE the objects from activity.participants - the wizard mutates them in
  // place and returns them - so identity matching works, with an id match as a fallback.
  const list = (activity && activity.participants) || []
  let index = list.indexOf(participant)
  if (index < 0) index = list.findIndex((p) => p && p.id && p.id === participant.id)
  return index >= 0 ? { participantIndex: index } : {}
}

function inputToCents(value) {
  const cleaned = String(value || '').replace(',', '.').replace(/[^0-9.]/g, '')
  const euros = parseFloat(cleaned)
  if (isNaN(euros)) return null
  return Math.round(euros * 100)
}

function ActivityPaymentDialog({ open, onClose, onSaved, activity, participant, firebase }) {
  const [amount, setAmount] = useState('')
  const [type, setType] = useState(DEFAULT_TYPE)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  // Reset on each open rather than on mount: the dialog instance is reused for every
  // participant, so without this the previous participant's edited amount carries over.
  // What is still owed: the participant's own (member) price minus what they have paid. A free
  // entry or a full payment leaves the field empty, so the same payment is not added twice.
  const suggestion = paymentSuggestion(activity, participant)

  useEffect(() => {
    if (open) {
      setAmount(centsToInput(paymentSuggestion(activity, participant).outstanding))
      setType(DEFAULT_TYPE)
      setError(null)
    }
  }, [open, activity, participant])

  const cents = inputToCents(amount)
  const valid = cents !== null && cents > 0

  const handleSave = async () => {
    if (!valid) return
    setSaving(true)
    setError(null)
    const response = await firebase.addActivityPayment(
      activity.id,
      participant.id,
      cents,
      type,
      participantIdentity(activity, participant)
    )
    // addActivityPayment resolves with { error } rather than throwing, so a failure never
    // reaches a catch - without this check the dialog would close as if it had worked.
    if (response && response.error) {
      setSaving(false)
      setError(response.error)
      return
    }
    setSaving(false)
    if (onSaved) onSaved()
    if (onClose) onClose()
  }

  const handleClose = () => {
    if (saving) return
    if (onClose) onClose()
  }

  const name = participant ? [participant.firstname, participant.lastname].filter(Boolean).join(' ') : ''
  const actname = activityName(activity)
  // payments[] hangs off the activity and is matched to a participant by uid.
  const existing = participant && activity && Array.isArray(activity.payments)
    ? activity.payments.filter((p) => p.uid === participant.id)
    : []

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth='xs'>
      <DialogTitle>Lisää maksu</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: 12 }}>
          {name}
          {actname ? <> – {actname}</> : ''}
        </div>
        {/* An existing payment does not block a new one: part payments are a real case.
            Show what is already there so the manager is not adding a duplicate blind. */}
        {existing.length > 0 && (
          <Alert severity='info' style={{ marginBottom: 12 }}>
            Osallistujalla on jo {existing.length === 1 ? 'maksu' : existing.length + ' maksua'}:{' '}
            {existing
              .map((p) => {
                const when = moment(p.timestamp, ['YYYY.MM.DD HH:mm', 'YYYY-MM-DD HH:mm:ss']).format('DD.MM.YYYY HH:mm')
                const sum = typeof p.amount === 'number' ? (p.amount / 100).toFixed(2).replace('.', ',') + ' €' : ''
                return [when, p.typelabel || p.type, sum].filter(Boolean).join(' ')
              })
              .join(', ')}
          </Alert>
        )}
        {suggestion.outstanding === 0 && (
          <Alert severity='info' style={{ marginBottom: 12 }}>
            {suggestion.source === 'entry' && suggestion.paid === 0
              ? 'Osallistuja on ilmoittautunut jäsenyyden vapaalla kerralla, joten maksettavaa ei ole.'
              : 'Osallistujalla ei ole maksettavaa (' + SOURCE_TEXT[suggestion.source] + ' ' + formatCents(suggestion.due) +
                ', maksettu ' + formatCents(suggestion.paid) + ').'}{' '}
            Voit silti kirjata summan itse.
          </Alert>
        )}
        <TextField
          fullWidth
          label='Summa'
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          disabled={saving}
          error={amount !== '' && !valid}
          helperText={
            amount !== '' && !valid
              ? 'Anna summa, esim. 12,50'
              : 'Hinta ' + formatCents(suggestion.due) + ' (' + SOURCE_TEXT[suggestion.source] + ')' +
                (suggestion.paid > 0 ? ', maksettu ' + formatCents(suggestion.paid) : '')
          }
          InputProps={{ endAdornment: <InputAdornment position='end'>€</InputAdornment> }}
          style={{ marginBottom: 12 }}
        />
        <TextField
          select
          fullWidth
          label='Maksutapa'
          value={type}
          onChange={(e) => setType(e.target.value)}
          disabled={saving || ACTIVITY_PAYMENT_TYPES.length < 2}
        >
          {ACTIVITY_PAYMENT_TYPES.map((pt) => (
            <MenuItem key={pt.value} value={pt.value}>
              {pt.label}
            </MenuItem>
          ))}
        </TextField>
        {error && (
          <Alert severity='error' style={{ marginTop: 12 }}>
            Maksun lisääminen epäonnistui: {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSave} color='primary' variant='contained' disabled={saving || !valid}>
          Tallenna {saving ? <CircularProgress size={14} /> : ''}
        </Button>
        <Button onClick={handleClose} disabled={saving}>
          Peruuta
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default withFirebase(ActivityPaymentDialog)
